// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Open Konvo on". Pure rules for where the app lands when it starts at
// the root URL (fresh load, PWA launch, desktop app start). The choice is per
// device and per form factor: the phone picks from its rail tabs, the desktop
// from the sidebar's page buttons. Destinations use the phone rail's ids so
// both surfaces share one vocabulary.

import {Routes} from '@app/app/Routes';
import type {MobileRailTabId} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';

export type StartFormFactor = 'phone' | 'desktop';

/** Places the app can open on. 'more' and 'reminders' are panels over a page, not places. */
export const START_DESTINATIONS = [
	'home',
	'chats',
	'mail',
	'calendar',
	'clickup',
	'whatsapp',
	'agents',
	'scheduled',
	'scratchpad',
] as const satisfies ReadonlyArray<MobileRailTabId>;

export type StartDestination = (typeof START_DESTINATIONS)[number];

/** 'last' reopens the last top-level destination used on this device. */
export type StartChoice = StartDestination | 'last';

/** What the app does today when nothing was chosen: the Chats list. */
export const DEFAULT_START_DESTINATION: StartDestination = 'chats';

/**
 * Places "Last used" may reopen on a phone even when they are not on the rail
 * (they were reached from More). The scratchpad is a drawer, so it is only an
 * explicit choice and never remembered.
 */
const PHONE_REMEMBERABLE: ReadonlyArray<StartDestination> = [
	'home',
	'chats',
	'mail',
	'calendar',
	'clickup',
	'whatsapp',
	'agents',
	'scheduled',
];

/** Something to do once the landing page is up (phone only). */
export type StartAction = 'whatsapp' | 'scratchpad';

export interface StartTarget {
	destination: StartDestination;
	path: string;
	action: StartAction | null;
}

export function isStartDestination(value: unknown): value is StartDestination {
	return typeof value === 'string' && (START_DESTINATIONS as ReadonlyArray<string>).includes(value);
}

export function isStartChoice(value: unknown): value is StartChoice {
	return value === 'last' || isStartDestination(value);
}

/** The phone's options: its rail tabs in rail order, minus the panels. */
export function phoneStartOptions(tabs: ReadonlyArray<MobileRailTabId>): Array<StartDestination> {
	return tabs.filter(isStartDestination);
}

export interface StartPathContext {
	formFactor: StartFormFactor;
	/** Phone: the rail tabs. WhatsApp only counts as its own place when it has a tab. */
	phoneTabs?: ReadonlyArray<MobileRailTabId>;
	/** The Chats list filter ('whatsapp' when the phone shows WhatsApp chats). */
	chatsFilter?: string;
}

/** Which top-level destination a route belongs to, for "Last used". Null for routes that are not one. */
export function startDestinationForPath(pathname: string, context: StartPathContext): StartDestination | null {
	if (Routes.isTodayRoute(pathname)) return 'home';
	if (Routes.isEmailsRoute(pathname)) return 'mail';
	if (Routes.isCalendarRoute(pathname)) return 'calendar';
	if (Routes.isTasksRoute(pathname)) return 'clickup';
	if (Routes.isWhatsAppRoute(pathname)) return 'whatsapp';
	if (Routes.isAgentsRoute(pathname)) return 'agents';
	if (Routes.isScheduledRoute(pathname)) return 'scheduled';
	if (context.formFactor === 'phone' && (context.phoneTabs ?? []).includes('whatsapp')) {
		// Mirrors the rail: WhatsApp opens as Chats filtered to it.
		if (pathname === Routes.DM_WHATSAPP || Routes.isDMWhatsAppRoute(pathname)) return 'whatsapp';
		if (pathname === Routes.ME && context.chatsFilter === 'whatsapp') return 'whatsapp';
	}
	if (Routes.isChannelRoute(pathname) || Routes.isSpecialPage(pathname)) return 'chats';
	return null;
}

/** Whether a destination may be remembered as "Last used" on this form factor. */
export function isRememberableStart(
	destination: StartDestination,
	formFactor: StartFormFactor,
	available: ReadonlyArray<StartDestination>,
): boolean {
	return formFactor === 'phone' ? PHONE_REMEMBERABLE.includes(destination) : available.includes(destination);
}

/** The route (and follow-up action) for a destination. */
export function startTargetFor(
	destination: StartDestination,
	formFactor: StartFormFactor,
	available: ReadonlyArray<StartDestination>,
): StartTarget {
	switch (destination) {
		case 'home':
			return {destination, path: Routes.TODAY, action: null};
		case 'mail':
			return {destination, path: Routes.EMAILS, action: null};
		case 'calendar':
			return {destination, path: Routes.CALENDAR, action: null};
		case 'clickup':
			return {destination, path: Routes.TASKS, action: null};
		case 'agents':
			return {destination, path: Routes.AGENTS, action: null};
		case 'scheduled':
			return {destination, path: Routes.SCHEDULED, action: null};
		case 'whatsapp':
			// The phone's WhatsApp tab is Chats filtered to WhatsApp; the desktop button opens its page.
			return formFactor === 'phone'
				? {destination, path: Routes.ME, action: 'whatsapp'}
				: {destination, path: Routes.WHATSAPP, action: null};
		case 'scratchpad':
			// The scratchpad is a drawer: open it over Home when Home is on the rail, else over Chats.
			return {destination, path: available.includes('home') ? Routes.TODAY : Routes.ME, action: 'scratchpad'};
		case 'chats':
			return {destination, path: Routes.ME, action: null};
	}
}

export interface StartRouteInput {
	formFactor: StartFormFactor;
	/** The saved choice; null when the user never picked one. */
	choice: StartChoice | null;
	/** The last top-level destination used on this device and form factor. */
	remembered: StartDestination | null;
	/** Destinations offered as explicit choices: the phone's rail tabs or the desktop sidebar pages. */
	available: ReadonlyArray<StartDestination>;
	/** The app was opened on a specific URL (notification, shared link, message link). It always wins. */
	isDeepLink: boolean;
}

/** Which destination a launch opens on, or null when a deep link must be left alone. */
export function resolveStartDestination(input: StartRouteInput): StartDestination | null {
	if (input.isDeepLink) return null;
	const {choice, remembered, available, formFactor} = input;
	if (choice === 'last') {
		if (remembered && isRememberableStart(remembered, formFactor, available)) return remembered;
		return DEFAULT_START_DESTINATION;
	}
	if (choice && available.includes(choice)) return choice;
	return DEFAULT_START_DESTINATION;
}

/** setting + remembered + available tabs + deep link -> where to land (null: leave the URL alone). */
export function resolveStartTarget(input: StartRouteInput): StartTarget | null {
	const destination = resolveStartDestination(input);
	return destination ? startTargetFor(destination, input.formFactor, input.available) : null;
}

/** The choice to show as selected in Settings: the saved one, else the default. */
export function effectiveStartChoice(choice: StartChoice | null): StartChoice {
	return choice ?? DEFAULT_START_DESTINATION;
}
